import { Box, Paper, Stack, Tab, Tabs, Typography } from '@mui/material'
import { AdCacheStatus } from '../../../component/shared'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { MembershipView } from '../../../component/admin/ad-management/membership'
import { MembershipList } from '../../../component/admin/ad-management/user/MembershipList'
import { LAST_MEMBERSHIPS_TAB_ACCESSED, useMemberships } from '../../../hooks/ad-management/use-memberships'
import { TADMembership } from '../../../type'
import { LocalStorageManager } from '../../../utility/local-storage-manager'
import { TEXT } from '../../../utility/text'
import { Icon } from '../../../component/icon'
import { TADMembershipTemplate } from '../../../type/ad-management'

export enum MembershipType {
  External = 'external',
  Internal = 'internal',
  Template = 'template',
}

interface ITabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function CustomTabPanel(props: Readonly<ITabPanelProps>) {
  const { children, value, index, ...other } = props

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ width: '100%' }}
      {...other}
    >
      {value === index && <Paper sx={{ p: 3 }}>{children}</Paper>}
    </div>
  )
}

const a11yProps = (index: number) => ({
  id: `users-tab-${index}`,
  'aria-controls': `users-tabpanel-${index}`,
})
const tabTypeMap: MembershipType[] = [MembershipType.External, MembershipType.Internal, MembershipType.Template]

export const MembershipsTabs = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [isMembershipView, setIsMembershipView] = useState(false)
  const searchParams = new URLSearchParams(location.search)
  const typeParam = searchParams.get('type') as MembershipType | null
  const initialTab = !typeParam || typeParam === MembershipType.External ? 0 : typeParam === MembershipType.Internal ? 1 : 2
  const [selectedTab, setSelectedTab] = useState(initialTab)
  const [membership, setMembership] = useState<TADMembership | TADMembershipTemplate>({} as TADMembership | TADMembershipTemplate)

  // Map tab index to MembershipType
  const currentType = tabTypeMap[selectedTab]

  const [bgRefreshKey, setBgRefreshKey] = useState(0)
  const { isLoading, memberships, setSearch, page, setPage, totalCount, maxPageSize, handleExportMembershipButtonClick, getMemberships } =
    useMemberships(currentType)

  const getMembershipsWithBgRefresh = useCallback(async () => {
    setBgRefreshKey((k) => k + 1)
    await getMemberships()
  }, [getMemberships])

  useEffect(() => {
    setSearch('')
    navigate(`?type=${currentType}`, { replace: true })
  }, [selectedTab, navigate, setSearch, currentType])

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
    setPage(1)
    LocalStorageManager.addItem(LAST_MEMBERSHIPS_TAB_ACCESSED, newValue)
    setBgRefreshKey((k) => k + 1)
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 3 }}>
        <Typography
          variant='h1'
          data-testid='screen-title'
        >
          {TEXT.screen.memberships.title}
        </Typography>
        <AdCacheStatus
          onRefresh={getMemberships}
          refreshTrigger={bgRefreshKey}
        />
      </Box>

      <Box>
        <Tabs
          value={selectedTab}
          onChange={handleChange}
          data-testid='user-list'
          aria-label='User list'
        >
          <Tab
            data-testid='external-users'
            aria-label='External user list'
            label={
              <Stack
                direction={'row'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Icon
                  name='MembershipExternal'
                  stroke='CurrentColor'
                  strokeWidth='2'
                  fill='none'
                />
                <span>External Memberships</span>
              </Stack>
            }
            {...a11yProps(0)}
          />
          <Tab
            data-testid='internal-users'
            aria-label='Internal user list'
            label={
              <Stack
                direction={'row'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Icon
                  name='MembershipInternal'
                  stroke='CurrentColor'
                  strokeWidth='2'
                  fill='none'
                />{' '}
                <span>Internal Memberships</span>
              </Stack>
            }
            {...a11yProps(1)}
          />
          <Tab
            data-testid='templates'
            aria-label='Templates list'
            label={
              <Stack
                direction={'row'}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Icon
                  name='Templates'
                  stroke='CurrentColor'
                  strokeWidth='2'
                  fill='none'
                />
                <span>Templates</span>
              </Stack>
            }
            {...a11yProps(2)}
          />
        </Tabs>
      </Box>
      <CustomTabPanel
        value={selectedTab}
        index={0}
      >
        <MembershipList
          membershipType={MembershipType.External}
          memberships={memberships}
          setSearch={setSearch}
          setMembership={setMembership}
          getMemberships={getMembershipsWithBgRefresh}
          totalCount={totalCount}
          page={page}
          maxPageSize={maxPageSize}
          setPage={setPage}
          setIsMembershipView={setIsMembershipView}
          isLoading={isLoading}
          handleExportMembershipButtonClick={() => handleExportMembershipButtonClick(MembershipType.External)}
        />
      </CustomTabPanel>
      <CustomTabPanel
        value={selectedTab}
        index={1}
      >
        <MembershipList
          membershipType={MembershipType.Internal}
          memberships={memberships}
          setSearch={setSearch}
          setMembership={setMembership}
          getMemberships={getMembershipsWithBgRefresh}
          totalCount={totalCount}
          page={page}
          maxPageSize={maxPageSize}
          setPage={setPage}
          setIsMembershipView={setIsMembershipView}
          isLoading={isLoading}
          handleExportMembershipButtonClick={() => handleExportMembershipButtonClick(MembershipType.Internal)}
        />
      </CustomTabPanel>
      <CustomTabPanel
        value={selectedTab}
        index={2}
      >
        <MembershipList
          membershipType={MembershipType.Template}
          memberships={memberships}
          setSearch={setSearch}
          setMembership={setMembership}
          totalCount={totalCount}
          page={page}
          getMemberships={getMembershipsWithBgRefresh}
          maxPageSize={maxPageSize}
          setPage={setPage}
          setIsMembershipView={setIsMembershipView}
          isLoading={isLoading}
          handleExportMembershipButtonClick={() => handleExportMembershipButtonClick(MembershipType.Template)}
        />
      </CustomTabPanel>
      <MembershipView
        isShow={isMembershipView}
        isExternal={currentType === MembershipType.External}
        membership={'GroupName' in membership ? membership.GroupName : 'name' in membership ? membership._id : ''}
        date={'WhenCreated' in membership ? membership.WhenCreated : 'created_at' in membership ? membership.created_at : new Date()}
        onCancel={() => setIsMembershipView(false)}
        type={currentType}
      />
    </>
  )
}
